import getDataserviceUrl from './dataservice-urls';
import fetchWithRetry, { ResultErrorVariant } from './retry';

export type FeatureFlagData = {
  featureSpec: string;
  featureName: string;
  featureData?: Record<string, unknown>;
};

type FeatureFlagArgs = {
  feature: FeatureFlagData;
  retry?: boolean;
  setFlagValue: (flagValue: boolean) => void;
};

const getFeatureFlagUrl = () => {
  const pathname = '/api/v1/features';
  return `${getDataserviceUrl(window.aui.env)}${pathname}`;
};

const getOneWebDomainFeatureSpec = (): string => {
  switch (window.aui.env) {
    case 'local':
    case 'ci':
    case 'dev':
      return 'one-web-domain.dev.json';
    case 'qa':
      return 'one-web-domain.qa.json';
    case 'sbx':
      return 'one-web-domain.sbx.json';
    default:
      return 'one-web-domain.prod.json';
  }
};

export const getSupercupFeatureSpec = (): string => {
  switch (window.aui.env) {
    case 'local':
    case 'ci':
    case 'dev':
      return 'supercup.ci.json';
    case 'qa':
      return 'supercup.qa.json';
    case 'sbx':
      return 'supercup.sbx.json';
    default:
      return 'supercup.prod.json';
  }
};

export const getNavigationFeatureSpec = (): string => {
  switch (window.aui.env) {
    case 'local':
    case 'ci':
    case 'dev':
      return 'navigation.webplatform.dev.json';
    case 'qa':
      return 'navigation.webplatform.qa.json';
    case 'sbx':
      return 'navigation.webplatform.sbx.json';
    default:
      return 'navigation.webplatform.prd.json';
  }
};

export const getGlobalSupportPortalUrl = (): string => {
  switch (window.aui.env) {
    case 'sbx':
    case 'prod':
    case 'prd':
      return 'https://community.avalara.com/support/services/auth/sso/AvalaraIdentity';
    case 'local':
    case 'ci':
    case 'dev':
    case 'qa':
    case 'uat':
    default:
      return 'https://avalara--fullqa.sandbox.my.site.com/SupportCases/services/auth/sso/AvalaraIdentity';
  }
};

export const getShouldUseAccountOwdFeatureFlagData = (): FeatureFlagData => ({
  featureSpec: getOneWebDomainFeatureSpec(),
  featureName: 'shouldUseAccountOwd',
});

export const getFeatureFlag = async ({ feature, retry = true, setFlagValue }: FeatureFlagArgs): Promise<void> => {
  const body = {
    config: {
      names: [feature.featureSpec],
    },
    data: feature.featureData,
  };

  const postData = {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const response = await fetchWithRetry<{ data: Record<string, boolean> }>({
    url: getFeatureFlagUrl(),
    options: postData,
    retries: retry ? 1 : 0,
    retryDelay: 500, // half a second
  });

  if (!response.ok) {
    const { error } = response as ResultErrorVariant;
    console.error('Failed to fetch feature flag:', error);
    setFlagValue(false);
    return;
  }

  const flagValue = !!response.value.data[feature.featureName];
  setFlagValue(flagValue);
};

const prodAvaAppUrl = 'https://app.avalara.com';

const getAvaAppUrl = (): string => {
  switch (window.aui.env) {
    case 'local':
      return 'http://localhost:8080';
    case 'ci':
    case 'dev':
      return 'https://avaapp.gamma.dev.us-west-2.aws.avalara.io';
    case 'qa':
    case 'uat':
      return 'https://avaapp.gamma.qa.us-west-2.aws.avalara.io';
    case 'sbx':
      return 'https://app.sbx.avalara.com';
    case 'prod':
    default:
      return prodAvaAppUrl;
  }
};

export const getAccountUrl = (shouldUseAccountOwd: boolean): string => {
  if (shouldUseAccountOwd) {
    if (window.aui.env === 'sbx') return `${prodAvaAppUrl}/account`;
    return `${getAvaAppUrl()}/account`;
  }

  switch (window.aui.env) {
    case 'local':
      return 'https://local.account.avalara.io/#';
    case 'ci':
    case 'dev':
      return 'https://ci.account.avalara.io/#';
    case 'qa':
    case 'uat':
      return 'https://qa.account.avalara.io/#';
    case 'sbx':
    case 'prod':
    default:
      return 'https://account.avalara.com/#';
  }
};
